import { useCallback, useEffect, useRef, useState } from 'react';
import {
  RoomEvent,
  Track,
  type RemoteAudioTrack,
  type RemoteTrack,
  type RemoteTrackPublication,
  type Room,
} from 'livekit-client';

import { Icon } from './Icon';
import './RoomAudio.css';

/**
 * Воспроизведение звука удалённых участников.
 *
 * livekit-client сам аудио не проигрывает — дорожку нужно привязать к
 * <audio>. Отдельная сложность — политика автозапуска: Safari, iOS и
 * webview Telegram блокируют звук, пока на странице не было жеста
 * пользователя. Молча потерять звук нельзя, поэтому при блокировке
 * показываем кнопку: одно нажатие включает всё сразу.
 */
export function RoomAudio({ room, speakerId }: { room: Room; speakerId?: string }) {
  const [tracks, setTracks] = useState<RemoteAudioTrack[]>([]);
  const [blocked, setBlocked] = useState(false);
  const elementsRef = useRef(new Set<HTMLAudioElement>());

  useEffect(() => {
    const collect = () => {
      const next: RemoteAudioTrack[] = [];
      room.remoteParticipants.forEach((participant) => {
        participant.trackPublications.forEach((publication) => {
          const track = publication.track;
          if (track && track.kind === Track.Kind.Audio) next.push(track as RemoteAudioTrack);
        });
      });
      setTracks(next);
    };

    collect();

    const onSubscribed = (track: RemoteTrack) => {
      if (track.kind === Track.Kind.Audio) collect();
    };
    const onUnsubscribed = (_: RemoteTrack, __: RemoteTrackPublication) => collect();

    // LiveKit сам сообщает, когда браузер отказался проигрывать звук.
    const onPlaybackChanged = () => setBlocked(!room.canPlaybackAudio);

    room.on(RoomEvent.TrackSubscribed, onSubscribed);
    room.on(RoomEvent.TrackUnsubscribed, onUnsubscribed);
    room.on(RoomEvent.ParticipantDisconnected, collect);
    room.on(RoomEvent.AudioPlaybackStatusChanged, onPlaybackChanged);

    return () => {
      room.off(RoomEvent.TrackSubscribed, onSubscribed);
      room.off(RoomEvent.TrackUnsubscribed, onUnsubscribed);
      room.off(RoomEvent.ParticipantDisconnected, collect);
      room.off(RoomEvent.AudioPlaybackStatusChanged, onPlaybackChanged);
    };
  }, [room]);

  const unlock = useCallback(async () => {
    try {
      // Вызов внутри обработчика клика — только так засчитывается жест.
      await room.startAudio();
      await Promise.all([...elementsRef.current].map((el) => el.play().catch(() => {})));
      setBlocked(false);
    } catch {
      // Останемся с кнопкой: пользователь попробует ещё раз.
    }
  }, [room]);

  const register = useCallback((element: HTMLAudioElement | null, attach: boolean) => {
    if (!element) return;
    if (attach) elementsRef.current.add(element);
    else elementsRef.current.delete(element);
  }, []);

  return (
    <>
      <div className="room-audio" aria-hidden>
        {tracks.map((track) => (
          <AudioSink
            key={track.sid ?? track.mediaStreamID}
            track={track}
            speakerId={speakerId}
            register={register}
          />
        ))}
      </div>

      {blocked && (
        <button type="button" className="audio-unlock" onClick={unlock}>
          <Icon name="volume" size={18} />
          Нажмите, чтобы включить звук
        </button>
      )}
    </>
  );
}

function AudioSink({
  track,
  speakerId,
  register,
}: {
  track: RemoteAudioTrack;
  speakerId?: string;
  register: (element: HTMLAudioElement | null, attach: boolean) => void;
}) {
  const ref = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    const element = ref.current;
    if (!element) return;

    track.attach(element);
    register(element, true);

    return () => {
      register(element, false);
      track.detach(element);
    };
  }, [track, register]);

  useEffect(() => {
    const element = ref.current as
      | (HTMLAudioElement & { setSinkId?: (id: string) => Promise<void> })
      | null;
    // Выбор устройства вывода умеют не все браузеры — Safari, например, нет.
    if (!element || !speakerId || typeof element.setSinkId !== 'function') return;
    element.setSinkId(speakerId).catch(() => {});
  }, [speakerId]);

  return <audio ref={ref} autoPlay playsInline />;
}
